import { NodeID } from "@hex-insights/app-modules";
import { ClassNameProps, Column, If, makeClassName, Row, StyleProps, TruncatedText } from "@hex-insights/core";
import { UserFormat } from "../../../Format";
import { GenericModelLink } from "../../../Links";
import { IconUser, UserIcon } from "../Icon";
import styles from "./styles.module.css";

export type UserIconNameDescriptionProps = {
	user: IconUser;
	linkID?: NodeID;
	description?: JSX.Element | string | null;
	size?: "small" | "medium" | "large";
	iconSize?: number;
	noLink?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function UserIconNameDescription({
	user,
	linkID,
	description,
	size = "medium",
	iconSize: iconSizeProp,
	noLink = false,
	className,
	style,
}: UserIconNameDescriptionProps) {
	const { iconSize, rowSpacing, columnSpacing } = getSizes(size);

	const content = (
		<Row
			justify="spaced-start"
			align="center"
			horizontalSpacing={rowSpacing}
			className={styles["icon-name-description"] + " " + styles[`icon-name-description--${size}`]}
		>
			<UserIcon user={user} size={iconSizeProp ?? iconSize} />

			<Column justify="spaced-start" verticalSpacing={columnSpacing} style={{ minWidth: 0 }}>
				<TruncatedText>
					<span className={styles["icon-name-description__name"]}>{UserFormat.name(user)}</span>
				</TruncatedText>
				<If condition={!!description}>
					<span className={styles["icon-name-description__description"]}>{description}</span>
				</If>
			</Column>
		</Row>
	);

	if (noLink) {
		return (
			<div className={className} style={style}>
				{content}
			</div>
		);
	}

	return (
		<GenericModelLink
			instance={{ id: linkID ?? user.id }}
			className={makeClassName("link--no-text-decoration", className)}
			style={style}
		>
			{content}
		</GenericModelLink>
	);
}

function getSizes(size: "small" | "medium" | "large") {
	switch (size) {
		case "small":
			return { iconSize: 40, rowSpacing: "0.5rem", columnSpacing: "0.125rem" };
		case "medium":
			return { iconSize: 52, rowSpacing: "0.75rem", columnSpacing: "0.125rem" };
		case "large":
			return { iconSize: 64, rowSpacing: "1rem", columnSpacing: "0.25rem" };
	}
}
